/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const setOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    savedName: string
    value: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, savedName, value} = action.payload
  const settings = getPipelineDraftEntry(state, pipelineId, branch)?.draft?.settings

  if (settings != null) {
    settings['output-parameters'] ??= {}
    settings['output-parameters'][savedName] = value
  }
}

export const renameOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    name: string
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, savedName, name} = action.payload

  if (name !== savedName) {
    const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)
    pipeline.renamed ??= {}
    pipeline.renamed!.outputParameters ??= {}
    pipeline.renamed!.outputParameters![savedName] = name
  } else if (
    getPipelineDraftEntry(state, pipelineId, branch)?.renamed?.outputParameters?.[savedName] != null
  ) {
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    delete pipeline.renamed!.outputParameters![savedName]
    deleteIfEmpty(pipeline.renamed, 'outputParameters') && deleteIfEmpty(pipeline, 'renamed')
  }
}

export const deleteOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    savedName: string
    branch: string
  }>
> = (state, action) => {
  const {branch, pipelineId, savedName} = action.payload
  const settings = getPipelineDraftEntry(state, pipelineId, branch)?.draft?.settings

  if (getPipelineDraftEntry(state, pipelineId, branch)?.added?.outputParameters?.[savedName]) {
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    delete settings?.['output-parameters']?.[savedName]
    delete pipeline.added!.outputParameters![savedName]
    deleteIfEmpty(pipeline.added, 'outputParameters') && deleteIfEmpty(pipeline, 'added')
    delete pipeline.renamed?.outputParameters?.[savedName]
    deleteIfEmpty(pipeline.renamed, 'outputParameters') && deleteIfEmpty(pipeline, 'renamed')
  } else {
    const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)
    pipeline.deleted ??= {}
    pipeline.deleted!.outputParameters ??= []
    pipeline.deleted!.outputParameters!.push(savedName)
  }
}

export const restoreOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; savedName: string; branch: string}>
> = (state, action) => {
  const {branch, pipelineId, savedName} = action.payload
  const {deleted} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}

  if (deleted?.outputParameters?.includes(savedName)) {
    deleted.outputParameters = deleted?.outputParameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'outputParameters') &&
      deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'deleted')
  }
}

export const addOutputParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; branch: string}>
> = (state, action) => {
  const {branch, pipelineId} = action.payload
  const settings = getPipelineDraftEntry(state, pipelineId, branch)?.draft?.settings

  if (settings != null) {
    settings['output-parameters'] ??= {}
    const key = generateFreeName({
      getName: iteration => `output_parameter_${iteration}`,
      usedNames: Object.keys(settings['output-parameters']),
      errorMessage: 'Cannot generate unique parameter key',
    })
    settings['output-parameters'][key] = ''
    renameOutputParameter(state, {
      type: '',
      payload: {pipelineId, name: '', savedName: key, branch},
    })
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    pipeline.added ??= {}
    pipeline.added!.outputParameters ??= {}
    pipeline.added!.outputParameters![key] = true
  }
}

export const clearAddedOutputParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; branch: string}>
> = (state, action) => {
  const {branch, pipelineId} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  const settings = getPipelineDraftEntry(state, pipelineId, branch)?.draft?.settings

  if (added?.outputParameters != null && settings != null) {
    Object.keys(added.outputParameters).forEach(key => {
      delete settings['output-parameters']?.[key]
    })
    delete added.outputParameters
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}

export const applyAddedOutputParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; branch: string}>
> = (state, action) => {
  const {pipelineId, branch} = action.payload
  const {added} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}

  if (added?.outputParameters != null) {
    delete added.outputParameters
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'added')
  }
}
